import socket from '../socket/socket';
import type { DataChannelInterface } from './remoteBoardAgent';

/** How long the runner gets to accept. It answers in one server round trip,
 *  with no ICE to work through, so a runner that has not answered by now is
 *  one that does not know this transport: it needs an update. */
const OPEN_TIMEOUT_MS = 15_000;

/**
 * A runner link relayed over the server's Socket.io connection, for networks
 * that block WebRTC. The channel has the same shape as a wrapped data channel,
 * so nothing above the connection hook knows which transport it is on. Shared
 * by web and mobile because, unlike WebRTC, nothing here differs by platform.
 */
export function openRunnerSocketLink(
  runnerSocketId: string,
  events: {
    /** The runner accepted; the channel is live. `linkId` is the id this
     *  attempt is logged under, for the caller's link report. */
    onOpen: (channel: DataChannelInterface, linkId: string) => void;
    /** The runner or the server closed the link after it opened. */
    onClose: () => void;
    /** It never opened: the server refused it, or the runner never answered.
     *  `failure` says which, in the words the runner selector shows. */
    onFailed: (failure: string, linkId: string) => void;
  }
): {
  /** Close from this side. Tells the runner, and fires no `onClose`. */
  close: () => void;
} {
  // One id per attempt, the same shape a WebRTC offer carries, so the server's
  // `[signal]` lines and the caller's report line up.
  const linkId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const handlers = new Set<(msg: string) => void>();
  const closeHandlers = new Set<() => void>();
  // The runner replays every active session the instant it opens, before
  // anything here has subscribed. Hold frames until something does.
  const earlyMessages: string[] = [];
  let open = false;
  let closed = false;

  const settle = () => {
    if (closed) return;
    closed = true;
    clearTimeout(timeout);
    detach();
    for (const handler of closeHandlers) handler();
  };

  const channel: DataChannelInterface = {
    send: (msg) => {
      if (!open || closed) return;
      socket.emit('runnerSocketData', {
        targetSocketId: runnerSocketId,
        linkId,
        data: msg
      });
    },
    onMessage: (handler) => {
      handlers.add(handler);
      if (earlyMessages.length > 0) {
        const drain = earlyMessages.splice(0);
        for (const msg of drain) handler(msg);
      }
      return () => {
        handlers.delete(handler);
      };
    },
    onClose: (handler) => {
      if (closed) {
        handler();
        return () => undefined;
      }
      closeHandlers.add(handler);
      return () => {
        closeHandlers.delete(handler);
      };
    }
  };

  const isOurs = (data: { fromSocketId: string; linkId: string }) =>
    data.fromSocketId === runnerSocketId && data.linkId === linkId;

  const onOpened = (data: { fromSocketId: string; linkId: string }) => {
    if (!isOurs(data) || closed) return;
    open = true;
    clearTimeout(timeout);
    events.onOpen(channel, linkId);
  };

  const onData = (data: {
    fromSocketId: string;
    linkId: string;
    data: string;
  }) => {
    if (!isOurs(data) || closed) return;
    if (handlers.size === 0) {
      earlyMessages.push(data.data);
      return;
    }
    for (const handler of handlers) handler(data.data);
  };

  const onRemoteClose = (data: { fromSocketId: string; linkId: string }) => {
    if (!isOurs(data)) return;
    settle();
    events.onClose();
  };

  const onSignalError = (data: { targetSocketId: string; reason: string }) => {
    if (data.targetSocketId !== runnerSocketId || open) return;
    settle();
    events.onFailed(`server refused the link: ${data.reason}`, linkId);
  };

  socket.on('runnerSocketOpened', onOpened);
  socket.on('runnerSocketData', onData);
  socket.on('runnerSocketClose', onRemoteClose);
  socket.on('runnerSignalError', onSignalError);

  function detach() {
    socket.off('runnerSocketOpened', onOpened);
    socket.off('runnerSocketData', onData);
    socket.off('runnerSocketClose', onRemoteClose);
    socket.off('runnerSignalError', onSignalError);
  }

  const timeout = setTimeout(() => {
    if (open || closed) return;
    // Tell the runner too, in case it is only slow: a link it opens later
    // would otherwise sit there with nobody on this end.
    socket.emit('runnerSocketClose', {
      targetSocketId: runnerSocketId,
      linkId
    });
    settle();
    events.onFailed(
      `runner did not answer in ${OPEN_TIMEOUT_MS / 1000}s — socket mode needs an up-to-date runner`,
      linkId
    );
  }, OPEN_TIMEOUT_MS);

  socket.emit('runnerSocketOpen', { targetSocketId: runnerSocketId, linkId });

  return {
    close: () => {
      if (closed) return;
      socket.emit('runnerSocketClose', {
        targetSocketId: runnerSocketId,
        linkId
      });
      settle();
    }
  };
}
